export const WORD_FONT_FAMILY = '"DINish"';
export const WORD_FONT_WEIGHT = 900;
export const WORD_MAX_WIDTH = 0.82;
export const WORD_MAX_HEIGHT = 0.34;

export interface NormalizedPoint {
  x: number;
  y: number;
}

export interface GlyphPlacement {
  id: number;
  character: string;
  center: NormalizedPoint;
  rotation: number;
}

export interface GlyphRenderLayout {
  placement: GlyphPlacement;
  fontSize: number;
  centerX: number;
  centerY: number;
  baseline: number;
  width: number;
  height: number;
}

export type GlyphInteractionMode = "translate" | "rotate";

interface TextMeasurement {
  width: number;
  actualBoundingBoxAscent: number;
  actualBoundingBoxDescent: number;
}

export interface MeasuringContext {
  font: string;
  fontKerning?: CanvasFontKerning;
  textAlign: CanvasTextAlign;
  textBaseline: CanvasTextBaseline;
  measureText(text: string): TextMeasurement;
}

interface GlyphRunMeasurement {
  advances: number[];
  kerningBefore: number[];
  width: number;
}

export function createGlyphPlacements(
  context: MeasuringContext,
  word: string,
  width: number,
  height: number,
  center: NormalizedPoint,
): GlyphPlacement[] {
  if (word.length === 0) {
    return [];
  }
  const fontSize = fitGlyphFontSize(context, word, width, height);
  configureTextMeasurement(context, fontSize);
  const run = measureGlyphRun(context, word);
  let cursor = clamp(center.x, 0, 1) * width - run.width / 2;

  return [...word].map((character, id) => {
    cursor += run.kerningBefore[id] ?? 0;
    const advance = run.advances[id] ?? 0;
    const placement: GlyphPlacement = {
      id,
      character,
      center: {
        x: (cursor + advance / 2) / width,
        y: clamp(center.y, 0, 1),
      },
      rotation: 0,
    };
    cursor += advance;
    return placement;
  });
}

export function layoutGlyphs(
  context: MeasuringContext,
  word: string,
  placements: readonly GlyphPlacement[],
  width: number,
  height: number,
): GlyphRenderLayout[] {
  if (placements.length === 0) {
    return [];
  }
  const fontSize = fitGlyphFontSize(context, word, width, height);
  configureTextMeasurement(context, fontSize);
  context.textAlign = "center";
  context.textBaseline = "alphabetic";

  return placements.map((placement) => {
    const metrics = context.measureText(placement.character);
    const centerX = placement.center.x * width;
    const centerY = placement.center.y * height;
    return {
      placement,
      fontSize,
      centerX,
      centerY,
      baseline:
        centerY +
        (metrics.actualBoundingBoxAscent -
          metrics.actualBoundingBoxDescent) /
          2,
      width: Math.max(1, metrics.width),
      height: Math.max(
        1,
        metrics.actualBoundingBoxAscent +
          metrics.actualBoundingBoxDescent,
      ),
    };
  });
}

export function classifyGlyphGrab(
  layout: GlyphRenderLayout,
  point: NormalizedPoint,
  centerThreshold = 0.7,
): GlyphInteractionMode {
  const deltaX = point.x - layout.centerX;
  const deltaY = point.y - layout.centerY;
  const cosine = Math.cos(layout.placement.rotation);
  const sine = Math.sin(layout.placement.rotation);
  const localX = cosine * deltaX + sine * deltaY;
  const localY = -sine * deltaX + cosine * deltaY;
  const radiusX = Math.max(1, layout.width / 2);
  const radiusY = Math.max(1, layout.height / 2);
  const normalizedDistance = Math.hypot(
    localX / radiusX,
    localY / radiusY,
  );
  return normalizedDistance <= centerThreshold ? "translate" : "rotate";
}

export function fitGlyphFontSize(
  context: MeasuringContext,
  word: string,
  width: number,
  height: number,
): number {
  if (word.length === 0) {
    return 1;
  }
  const referenceSize = 100;
  configureTextMeasurement(context, referenceSize);
  const referenceWidth = measureGlyphRun(context, word).width;
  const wordMetrics = context.measureText(word);
  const referenceHeight = Math.max(
    1,
    wordMetrics.actualBoundingBoxAscent +
      wordMetrics.actualBoundingBoxDescent,
  );

  return Math.max(
    1,
    Math.min(
      (width * WORD_MAX_WIDTH * referenceSize) /
        Math.max(1, referenceWidth),
      (height * WORD_MAX_HEIGHT * referenceSize) / referenceHeight,
    ),
  );
}

export function fontDeclaration(fontSize: number): string {
  return `${WORD_FONT_WEIGHT} ${fontSize}px ${WORD_FONT_FAMILY}, sans-serif`;
}

function configureTextMeasurement(
  context: MeasuringContext,
  fontSize: number,
): void {
  context.font = fontDeclaration(fontSize);
  if (context.fontKerning !== undefined) {
    context.fontKerning = "normal";
  }
}

function measureGlyphRun(
  context: MeasuringContext,
  word: string,
): GlyphRunMeasurement {
  const characters = [...word];
  const advances = characters.map(
    (character) => context.measureText(character).width,
  );
  const kerningBefore = characters.map((character, index) => {
    if (index === 0) {
      return 0;
    }
    const previousCharacter = characters[index - 1] ?? "";
    const previousAdvance = advances[index - 1] ?? 0;
    const currentAdvance = advances[index] ?? 0;
    const pairWidth = context.measureText(
      previousCharacter + character,
    ).width;
    const adjustment = pairWidth - previousAdvance - currentAdvance;
    return Number.isFinite(adjustment) ? adjustment : 0;
  });
  const width = advances.reduce(
    (sum, advance, index) =>
      sum + advance + (kerningBefore[index] ?? 0),
    0,
  );
  return { advances, kerningBefore, width: Math.max(0, width) };
}

export function sanitizeWord(value: string): string {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9 _-]/g, "")
    .replace(/\s+/g, " ")
    .slice(0, 18);
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}
